import {

    Briefcase,

    ChevronDown,

    IdCard,

    MapPin,

    Pencil,

    Phone,

    ShieldCheck,

    StickyNote,

    User,

} from 'lucide-react';

import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import Badge from '../ui/Badge';

import {

    deriveNationality,

    displayValue,

    formatProfileDate,

    parseExtendedProfile,

} from '../../utils/extendedProfile';



function ProfileField({ label, value, highlight = false, auto = false }) {

    return (

        <div

            className={`rounded-xl border px-4 py-3 ${

                highlight

                    ? 'border-emerald-200/80 bg-emerald-50/70 dark:border-emerald-500/20 dark:bg-emerald-950/20'

                    : auto

                        ? 'border-sky-200/80 bg-sky-50/60 dark:border-sky-500/20 dark:bg-sky-950/20'

                        : 'border-gray-100 bg-gray-50/90 dark:border-white/5 dark:bg-gray-900/60'

            }`}

        >

            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-white/50">{label}</p>

            <p className="mt-1.5 text-sm font-medium text-gray-900 dark:text-white break-words">{value || '-'}</p>

        </div>

    );

}



function ProfileSection({

    icon: Icon,

    title,

    description,

    badge,

    children,

    className = '',

    onEdit,

    canModify = false,

}) {

    const [open, setOpen] = useState(false);

    const panelId = useMemo(

        () => `profile-section-${title.replace(/\s+/g, '-').toLowerCase()}`,

        [title],

    );



    return (

        <section

            className={`overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-800 ${className}`}

        >

            <div className={`flex w-full flex-wrap items-start justify-between gap-3 bg-gradient-to-r from-gray-50 to-white px-5 py-4 dark:from-gray-900/80 dark:to-gray-800 ${open ? 'border-b border-gray-100 dark:border-white/5' : ''}`}>

                <button

                    type="button"

                    onClick={() => setOpen((prev) => !prev)}

                    aria-expanded={open}

                    aria-controls={panelId}

                    className="flex min-w-0 flex-1 items-start gap-3 text-left transition-colors hover:opacity-90"

                >

                    {Icon ? (

                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">

                            <Icon size={18} aria-hidden />

                        </span>

                    ) : null}

                    <div className="min-w-0">

                        <h3 className="text-base font-semibold text-gray-900 dark:text-white lg:text-lg">{title}</h3>

                        {description ? (

                            <p className="mt-1 text-sm text-gray-500 dark:text-white/60">{description}</p>

                        ) : null}

                    </div>

                </button>

                <div className="flex shrink-0 items-center gap-2">

                    {badge}

                    {canModify && onEdit ? (

                        <button

                            type="button"

                            onClick={onEdit}

                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-indigo-600 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40"

                        >

                            <Pencil size={16} />

                        </button>

                    ) : null}

                    <button

                        type="button"

                        onClick={() => setOpen((prev) => !prev)}

                        className={`flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-transform duration-200 dark:text-white/60 ${open ? 'rotate-180' : ''}`}

                    >

                        <ChevronDown size={18} />

                    </button>

                </div>

            </div>

            {open ? (

                <div id={panelId} className="p-5">

                    {children}

                </div>

            ) : null}

        </section>

    );

}



function formatPhone(phone) {

    if (!phone || !String(phone).trim()) return null;

    const digits = String(phone).replace(/\D/g, '');

    if (digits.length <= 3) return `+${digits}`;

    return `+${digits}`;

}



export default function MemberProfileTab({ member, canModify, onEditProfile, onEditExtendedSection }) {

    const { t } = useTranslation();

    const profile = parseExtendedProfile(member?.extended_profile);

    const { contacts, personal, address, identity, health, professional } = profile;

    const nationality = personal.nationality || deriveNationality(personal.citizenship);



    const roleLabel = member.role_in_gym === 'trainer'

        ? t('gym.membersManager.memberCard.trainer')

        : t('gym.membersManager.memberCard.member');



    const countryLabel = (code) => {

        if (!code) return null;

        return deriveNationality(code) || code;

    };



    return (

        <div className="w-full space-y-4 pb-4">

            <ProfileSection icon={User} title={t('gym.membersManager.memberDetail.profile.sections.managed.title')} description={t('gym.membersManager.memberDetail.profile.sections.managed.description')} badge={<Badge variant="success">{t('gym.membersManager.memberDetail.profile.activeBadge')}</Badge>} onEdit={onEditProfile} canModify={canModify}>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">

                    <ProfileField highlight label={t('gym.membersManager.memberDetail.profile.fields.name')} value={member.name} />

                    <ProfileField highlight label={t('gym.membersManager.memberDetail.profile.fields.email')} value={member.email} />

                    <ProfileField highlight label={t('gym.membersManager.memberDetail.profile.fields.username')} value={member.username} />

                    <ProfileField highlight label={t('gym.membersManager.memberDetail.profile.fields.phone')} value={formatPhone(member.phone)} />

                    <ProfileField highlight label={t('gym.membersManager.memberDetail.profile.fields.role')} value={roleLabel} />

                    <ProfileField highlight label={t('gym.membersManager.memberDetail.profile.fields.joinedAt')} value={member.joined_at ? new Date(member.joined_at).toLocaleDateString('it-IT') : null} />

                    <ProfileField highlight label={t('gym.membersManager.memberDetail.profile.fields.lastAccess')} value={member.last_login ? new Date(member.last_login).toLocaleDateString('it-IT') : t('gym.membersManager.memberDetail.never')} />

                    <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/70 px-4 py-3 dark:border-emerald-500/20 dark:bg-emerald-950/20">

                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-white/50">{t('gym.membersManager.memberDetail.profile.fields.setupStatus')}</p>

                        <div className="mt-1.5">

                            {member.setup_status === 'setup_incomplete' ? <Badge variant="warning">{t('gym.membersManager.memberDetail.profile.setupIncomplete')}</Badge> : <Badge variant="success">{t('gym.membersManager.memberDetail.profile.setupComplete')}</Badge>}

                        </div>

                    </div>

                </div>

            </ProfileSection>



            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">

                <ProfileSection icon={Phone} title={t('gym.membersManager.memberDetail.profile.sections.contacts.title')} description={t('gym.membersManager.memberDetail.profile.sections.contacts.description')} onEdit={() => onEditExtendedSection?.('contacts')} canModify={canModify}>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.landline')} value={displayValue(contacts.landline)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.secondaryEmail')} value={displayValue(contacts.secondary_email)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.emergencyContact')} value={displayValue(contacts.emergency_contact)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.emergencyPhone')} value={formatPhone(contacts.emergency_phone)} />

                    </div>

                </ProfileSection>

                <ProfileSection icon={User} title={t('gym.membersManager.memberDetail.profile.sections.personal.title')} description={t('gym.membersManager.memberDetail.profile.sections.personal.description')} onEdit={() => onEditExtendedSection?.('personal')} canModify={canModify}>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.birthDate')} value={formatProfileDate(personal.birth_date)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.gender')} value={personal.gender === 'M' ? 'Maschio' : personal.gender === 'F' ? 'Femmina' : null} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.birthPlace')} value={displayValue(personal.birth_place)} />
                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.birthPlaceZip')} value={displayValue(personal.birth_place_zip)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.citizenship')} value={countryLabel(personal.citizenship)} />

                        <ProfileField auto label={t('gym.membersManager.memberDetail.profile.fields.nationality')} value={displayValue(nationality)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.fiscalCode')} value={displayValue(personal.fiscal_code)} />

                    </div>

                </ProfileSection>

            </div>



            <ProfileSection icon={MapPin} title={t('gym.membersManager.memberDetail.profile.sections.address.title')} description={t('gym.membersManager.memberDetail.profile.sections.address.description')} onEdit={() => onEditExtendedSection?.('address')} canModify={canModify}>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">

                    <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.address')} value={displayValue(address.street)} />

                    <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.city')} value={displayValue(address.city)} />

                    <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.province')} value={displayValue(address.province)} />

                    <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.zip')} value={displayValue(address.zip)} />

                    <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.country')} value={countryLabel(address.country)} />

                </div>

            </ProfileSection>



            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">

                <ProfileSection icon={IdCard} title={t('gym.membersManager.memberDetail.profile.sections.identity.title')} description={t('gym.membersManager.memberDetail.profile.sections.identity.description')} onEdit={() => onEditExtendedSection?.('identity')} canModify={canModify}>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.documentType')} value={displayValue(identity.document_type)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.documentNumber')} value={displayValue(identity.document_number)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.documentIssueDate')} value={formatProfileDate(identity.document_issue_date)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.documentExpiry')} value={formatProfileDate(identity.document_expiry)} />

                    </div>

                </ProfileSection>

                <ProfileSection icon={ShieldCheck} title={t('gym.membersManager.memberDetail.profile.sections.health.title')} description={t('gym.membersManager.memberDetail.profile.sections.health.description')} onEdit={() => onEditExtendedSection?.('health')} canModify={canModify}>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.medicalCertificate')} value={displayValue(health.medical_certificate)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.medicalCertExpiry')} value={formatProfileDate(health.medical_cert_expiry)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.insuranceNumber')} value={displayValue(health.insurance_number)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.insuranceExpiry')} value={formatProfileDate(health.insurance_expiry)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.attachments')} value={displayValue(health.attachments)} />

                    </div>

                </ProfileSection>

            </div>



            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">

                <ProfileSection icon={Briefcase} title={t('gym.membersManager.memberDetail.profile.sections.professional.title')} description={t('gym.membersManager.memberDetail.profile.sections.professional.description')} onEdit={() => onEditExtendedSection?.('professional')} canModify={canModify}>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.profession')} value={displayValue(professional.profession)} />

                        <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.company')} value={displayValue(professional.company)} />

                    </div>

                </ProfileSection>

                <ProfileSection icon={StickyNote} title={t('gym.membersManager.memberDetail.profile.sections.notes.title')} description={t('gym.membersManager.memberDetail.profile.sections.notes.description')} onEdit={() => onEditExtendedSection?.('notes')} canModify={canModify}>

                    <ProfileField label={t('gym.membersManager.memberDetail.profile.fields.internalNotes')} value={displayValue(member.membership_notes)} />

                </ProfileSection>

            </div>

        </div>

    );

}


